<template>
  <div class="substep-approve-timesheet">
    <div class="substep-header">
      <h3 data-testId="step-title">Approve a Timesheet</h3>
      <p>Review what your team worked on and how much they spent on tasks before you approve their timesheet.</p>
    </div>
    <div class="select-team-member">
      <AppSelect
        v-model="teamMember"
        has-tooltip
        :options="teamMembers"
        tooltip-title="Select a team member to approve their timesheet"
        :select2-options="{ placeholder: 'Team member' }"
      />

      <AppButton
        v-if="teamMember && !isApprovedTimesheet"
        class="approve-timesheet"
        type="outline"
        :disabled="!teamMember"
        @click="approveTimesheet"
      >
        Approve Timesheet
      </AppButton>
    </div>
    <div class="substep-body">
      <div
        v-if="!teamMember"
        class="card-image"
      >
        <img
          :src="getImageAsset('no_team_member_selected').src"
          :alt="getImageAsset('no_team_member_selected').alt"
        />
        <p>No team member selected</p>
      </div>
      <div
        v-if="teamMember && isApprovedTimesheet"
        class="card-image"
      >
        <img
          rel="preload"
          :src="getImageAsset('timesheet_approved').src"
          :alt="getImageAsset('timesheet_approved').alt"
        />
        <p>Timesheet approved</p>
      </div>
      <div
        v-if="showActivities"
        class="activities-section"
      >
        <AppTable
          :table-data="teamMemberActivities"
          class="activities-table"
        >
          <AppTableColumn
            v-slot="{ row }"
            header="Project"
          >
            <AppAvatarInitial
              size="medium"
              :text="row.project"
            />
            <span class="project-name">{{ row.project }}</span>
            <p class="organization-name">{{ row.organization_name }}</p>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header-classes="col-manual"
            cell-classes="col-manual"
            header="Manual"
          >
            <span>{{ row.manual }}</span>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Total Worked"
          >
            <span>{{ row.total_worked }}</span>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header-classes="col-time-span"
            cell-classes="col-time-span"
            header="Time Span"
          >
            <span>{{ row.time_span }}</span>
          </AppTableColumn>
          <AppTableColumn
            v-slot="{ row }"
            header="Screenshots"
          >
            <a
              class="view-screenshot"
              href="#"
              @click="toggleScreenshot(row)"
            >
              <span>View Screenshot</span>
            </a>
          </AppTableColumn>
        </AppTable>

        <AppDialog
          :show="showScreenshot"
          :title="screenshotTitle"
          position="centered"
          class="getting-started-dialog-screenshot"
          append-to-body
          size="medium"
          @close="toggleScreenshot"
        >
          <h3>
            <AppAvatarInitial
              v-if="selectedRow"
              size="medium"
              :text="selectedRow.project"
            />
            {{ selectedRow?.project }}
          </h3>
          <a
            :href="getScreenshotUrl"
            target="_blank"
          >
            <img :src="getScreenshotUrl" />
          </a>
        </AppDialog>
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import AppTable from '~/components/AppTable.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import { GETTING_STARTED } from '~/app_constants';

export default {
  name: 'ApproveATimesheet',
  components: {
    AppTable,
    AppAvatarInitial,
  },
  mixins: [GettingStartedMixin],
  props: {
    teamMembers: {
      type: Array,
      default: () => [],
      validator: (teamMembers) => {
        return teamMembers.every((teamMember) => {
          return typeof teamMember.id === 'number' && typeof teamMember.text === 'string';
        });
      },
    },
    activities: {
      type: Object,
      default: () => ({}),
      validator: (activities) => {
        return Object.keys(activities).every((key) => {
          return activities[key].every((activity) => {
            return activity.project && activity.manual && activity.total_worked && activity.time_span;
          });
        });
      },
    },
  },
  data: () => ({
    teamMember: null,
    approvedTimesheets: [],
    showScreenshot: false,
    selectedRow: null,
  }),
  computed: {
    isApprovedTimesheet() {
      return this.approvedTimesheets.includes(this.teamMember);
    },
    getScreenshotUrl() {
      if (this.selectedRow) {
        return GETTING_STARTED.screenshots[this.selectedRow.id];
      }

      return null;
    },
    showActivities() {
      return this.teamMember && !this.isApprovedTimesheet;
    },
    teamMemberActivities() {
      if (this.teamMember && this.activities) {
        return this.activities[this.teamMember.toString()];
      }

      return [];
    },
    screenshotTitle() {
      const teamMemberName = this.teamMembers.find(member => member.id.toString() === this.teamMember.toString())?.text;

      return `${teamMemberName}'s Screenshot`;
    }
  },
  mounted() {
    this.preLoadImages();
  },
  methods: {
    getImageAsset(name) {
      return GETTING_STARTED[name];
    },
    approveTimesheet() {
      if (this.teamMember) {
        this.approvedTimesheets.push(this.teamMember);
        this.$emit('completeSubstep');
      }
    },
    toggleScreenshot(row) {
      if (row) {
        this.selectedRow = row;
      }
      this.showScreenshot = !this.showScreenshot;
      event.preventDefault();
    },
    preLoadImages() {
      Object.keys(GETTING_STARTED.screenshots).forEach((key) => {
        const img = new Image();

        img.src = GETTING_STARTED.screenshots[key];
      });
    },
  },
};
</script>
